// The left rail: tools for the canvas, the doors to adding things and to your
// data, and whatever else someone keeps there. What it holds and in what order
// is theirs (RailEditor); the six drawing tools sit behind one Draw button.
// Bottom right: zoom.

import { Fragment, useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { Brush, Ellipsis, Minus, Plus, Scan } from "lucide-react";
import { boardItems, get, useStore } from "../state/store";
import { bounds, itemRect } from "../canvas/geometry";
import { withMap } from "../boards/mindmap/live";
import { camera, useCamera } from "../canvas/camera";
import { groupOf, openRailEditor, RAIL_GROUPS, useRail, type RailId } from "../state/rail";
import { DRAW_TOOLS, drawTool, pickTool, RAIL_ITEMS } from "./railItems";
import { RailEditor } from "./RailEditor";
import { IconButton, useScrollEnds } from "./ui";
import { cx } from "../lib/util";

const tipOf = (id: RailId) => {
  const it = RAIL_ITEMS[id];
  const name = it.tip ?? it.label;
  return it.kbd ? `${name}  ${it.kbd}` : name;
};

/** aria-keyshortcuts takes key names; the single letters are already that. */
const keyName = (kbd?: string) => (kbd && /^[A-Z]$/.test(kbd) ? kbd : undefined);

export function Rail() {
  const slots = useRail((r) => r.slots);
  const editing = useRail((r) => r.editing);
  const tool = useStore((s) => s.tool);
  const panel = useStore((s) => s.panel);
  const hidden = useStore((s) => s.focusMode || s.presenting);
  // the drawing tools' flyout, and whether the keyboard opened it (then the focus goes in)
  const [fly, setFly] = useState<{ keys: boolean } | null>(null);
  const rail = useRef<HTMLDivElement>(null);
  const list = useRef<HTMLDivElement>(null);
  const drawBtn = useRef<HTMLButtonElement>(null);
  const editBtn = useRef<HTMLButtonElement>(null);
  const groups = RAIL_GROUPS.map((g) => slots.filter((s) => s.on && groupOf(s.id) === g)).filter((g) => g.length > 0);
  const count = groups.reduce((n, g) => n + g.length, 0);
  const current = drawTool(tool);
  const tip = useRailTip(rail, !!fly || editing, (id) => (id === "edit" ? "Customize toolbar" : id === "draw" ? (current ? `${current.label}  ${current.kbd}` : "Draw  P") : tipOf(id as RailId)));
  const ends = useScrollEnds(list, count);

  // a tool picked any other way (its key, Esc) closes the flyout
  useEffect(() => setFly(null), [tool]);
  // and a panel opening over them (A, D, ? or full screen) closes both
  useEffect(() => {
    if (!hidden && !panel) return;
    setFly(null);
    openRailEditor(false);
  }, [hidden, panel]);

  const closeFly = useCallback((refocus: boolean) => {
    setFly(null);
    if (refocus) drawBtn.current?.focus();
  }, []);
  const closeEditor = useCallback((refocus: boolean) => {
    openRailEditor(false);
    if (refocus) editBtn.current?.focus();
  }, []);

  // a vertical toolbar: up and down walk it, and while a button here has focus
  // the board's own Space, Enter and arrows wait (no widget behind gets nudged)
  const onKey = (e: React.KeyboardEvent) => {
    if ([" ", "Enter", "ArrowLeft", "ArrowRight"].includes(e.key)) return e.stopPropagation();
    if (!["ArrowDown", "ArrowUp", "Home", "End"].includes(e.key)) return;
    const btns = [...(rail.current?.querySelectorAll<HTMLButtonElement>("button") ?? [])];
    const i = btns.indexOf(document.activeElement as HTMLButtonElement);
    if (i < 0) return;
    e.preventDefault();
    e.stopPropagation();
    const n = btns.length;
    btns[e.key === "Home" ? 0 : e.key === "End" ? n - 1 : (i + (e.key === "ArrowDown" ? 1 : n - 1)) % n].focus();
  };

  // a click leaves the focus on the board, so Space still pans there and never presses the button again
  const clicked = (e: React.MouseEvent<HTMLButtonElement>) => e.detail > 0 && e.currentTarget.blur();

  return (
    <>
      <div
        ref={rail}
        className="rail island"
        role="toolbar"
        aria-label="Tools"
        aria-orientation="vertical"
        onKeyDown={onKey}
        // a right click on a toolbar is where people look for changing it
        onContextMenu={(e) => {
          e.preventDefault();
          openRailEditor();
        }}
        {...tip.handlers}
      >
        <div
          ref={list}
          className={cx("rail-list", ends)}
          style={{ "--n": count, "--s": groups.length - 1 } as React.CSSProperties}
          onScroll={() => {
            setFly(null);
            tip.hide();
          }}
        >
          {groups.map((g, i) => (
            <Fragment key={groupOf(g[0].id)}>
              {i > 0 && <span className="rail-sep" />}
              {g.map(({ id }) => {
                if (id === "draw")
                  return (
                    <button
                      key={id}
                      ref={drawBtn}
                      className={cx("icon-btn rail-draw", current && "is-active", fly && "is-open")}
                      aria-label={current ? `Draw, ${current.label}` : "Draw"}
                      aria-haspopup="menu"
                      aria-expanded={!!fly}
                      data-rail={id}
                      data-tool={current?.tool}
                      onClick={(e) => {
                        clicked(e);
                        setFly(fly ? null : { keys: e.detail === 0 });
                      }}
                      onKeyDown={(e) => {
                        if (e.key !== "ArrowRight") return;
                        e.preventDefault();
                        e.stopPropagation();
                        setFly({ keys: true });
                      }}
                    >
                      {current ? <current.Icon size={16} /> : <Brush size={16} />}
                      <i className="rail-more" aria-hidden />
                    </button>
                  );
                const it = RAIL_ITEMS[id];
                const on = !!it.active?.({ tool, panel });
                return (
                  <button
                    key={id}
                    className={cx("icon-btn", on && "is-active", id === "gallery" && "rail-add")}
                    aria-label={it.tip ?? it.label}
                    aria-pressed={it.active ? on : undefined}
                    aria-keyshortcuts={keyName(it.kbd)}
                    data-rail={id}
                    onClick={(e) => {
                      clicked(e);
                      it.run();
                    }}
                  >
                    {it.icon(16)}
                  </button>
                );
              })}
            </Fragment>
          ))}
        </div>
        <button
          ref={editBtn}
          className={cx("rail-edit", editing && "is-active")}
          aria-label="Customize toolbar"
          aria-haspopup="dialog"
          aria-expanded={editing}
          data-rail="edit"
          onClick={(e) => {
            clicked(e);
            openRailEditor(!editing);
          }}
        >
          <Ellipsis size={15} />
        </button>
        {tip.el}
      </div>
      {fly && drawBtn.current && <DrawFlyout anchor={drawBtn.current} viaKeys={fly.keys} onClose={closeFly} />}
      {editing && !hidden && <RailEditor anchor={editBtn.current} onClose={closeEditor} />}
    </>
  );
}

/** The drawing tools, in a small island beside the rail's Draw button. */
function DrawFlyout({ anchor, viaKeys, onClose }: { anchor: HTMLElement; viaKeys: boolean; onClose: (refocus: boolean) => void }) {
  const tool = useStore((s) => s.tool);
  const ref = useRef<HTMLDivElement>(null);
  const opened = useRef(tool);

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const a = anchor.getBoundingClientRect();
    const rail = anchor.closest(".rail")?.getBoundingClientRect() ?? a;
    el.style.left = `${rail.right + 8}px`;
    el.style.top = `${Math.max(8, Math.min(a.top + a.height / 2 - el.offsetHeight / 2, window.innerHeight - 8 - el.offsetHeight))}px`;
    // from the keyboard, the focus goes in: to the tool in use, or the first
    if (viaKeys) (el.querySelector<HTMLElement>('[aria-checked="true"]') ?? el.querySelector<HTMLElement>("button"))?.focus();
  }, [anchor, viaKeys]);

  // a tool picked by its key while the keyboard is in here: the focus goes back to Draw
  useEffect(() => {
    if (tool === opened.current) return;
    const f = document.activeElement;
    onClose(!!f && !!ref.current?.contains(f) && f.matches(":focus-visible"));
  }, [tool, onClose]);

  useEffect(() => {
    const down = (e: PointerEvent) => {
      const t = e.target as Node;
      if (!ref.current?.contains(t) && !anchor.contains(t)) onClose(false);
    };
    const key = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      e.preventDefault();
      e.stopPropagation();
      onClose(ref.current?.contains(document.activeElement) ?? false);
    };
    const away = () => onClose(false);
    window.addEventListener("pointerdown", down, true);
    window.addEventListener("keydown", key, true);
    window.addEventListener("resize", away);
    return () => {
      window.removeEventListener("pointerdown", down, true);
      window.removeEventListener("keydown", key, true);
      window.removeEventListener("resize", away);
    };
  }, [anchor, onClose]);

  const onKey = (e: React.KeyboardEvent) => {
    if (e.key === " " || e.key === "Enter") return e.stopPropagation();
    if (e.key === "Tab") {
      e.preventDefault();
      return onClose(true);
    }
    const btns = [...(ref.current?.querySelectorAll<HTMLButtonElement>("button") ?? [])];
    const i = btns.indexOf(document.activeElement as HTMLButtonElement);
    const n = btns.length;
    const to = { ArrowRight: i + 1, ArrowDown: i + 1, ArrowLeft: i - 1, ArrowUp: i - 1, Home: 0, End: n - 1 }[e.key];
    if (to === undefined || i < 0) return;
    e.preventDefault();
    e.stopPropagation();
    btns[(to + n) % n].focus();
  };

  return (
    <div ref={ref} className="rail-fly island" role="menu" aria-label="Drawing tools" aria-orientation="horizontal" onKeyDown={onKey}>
      {DRAW_TOOLS.map((t) => (
        <button
          key={t.tool}
          role="menuitemradio"
          aria-checked={tool === t.tool}
          aria-label={t.label}
          aria-keyshortcuts={t.kbd}
          className={cx("icon-btn", tool === t.tool && "is-active")}
          data-tip={`${t.label}  ${t.kbd}`}
          onClick={(e) => {
            // the tool in use, picked again, is put down
            pickTool(tool === t.tool ? "select" : t.tool);
            onClose(e.detail === 0);
          }}
        >
          <t.Icon size={16} />
          <span className="rail-fly-key" aria-hidden>
            {t.kbd}
          </span>
        </button>
      ))}
    </div>
  );
}

/**
 * The rail's tooltips, in the same pill as everywhere else. The rail can
 * scroll on a short window, and a scrolling box would cut a button's own tip
 * off, so the rail draws one for whichever button is pointed at or focused.
 */
export function useRailTip(rail: React.RefObject<HTMLDivElement | null>, off: boolean, text: (id: string) => string) {
  const [tip, setTip] = useState<{ id: string; y: number; n: number } | null>(null);
  const seq = useRef(0);
  const show = (target: EventTarget | null) => {
    const root = rail.current;
    const b = (target as Element | null)?.closest?.<HTMLElement>("[data-rail]");
    if (!root || !b || !root.contains(b)) return setTip(null);
    const id = b.dataset.rail!;
    setTip((t) => {
      if (t?.id === id) return t;
      const r = b.getBoundingClientRect();
      return { id, y: r.top + r.height / 2 - root.getBoundingClientRect().top - root.clientTop, n: ++seq.current };
    });
  };
  const hide = () => setTip(null);
  return {
    hide,
    handlers: {
      onPointerOver: (e: React.PointerEvent) => e.pointerType !== "touch" && show(e.target),
      onPointerLeave: hide,
      onFocus: (e: React.FocusEvent) => (e.target as Element).matches(":focus-visible") && show(e.target),
      onBlur: hide,
    },
    el:
      tip && !off ? (
        <span key={tip.n} className="rail-tip" style={{ top: tip.y }} aria-hidden>
          {text(tip.id)}
        </span>
      ) : null,
  };
}

export function ZoomControls({ simple = false }: { simple?: boolean }) {
  const z = useCamera((c) => c.z);
  return (
    <div className="zoom island">
      <IconButton label="Zoom out" kbd="−" onClick={() => camera.zoomStep(1 / 1.25)}>
        <Minus size={14} />
      </IconButton>
      <button className="zoom-value" data-tip="Reset to 100%  0" onClick={() => camera.zoomTo(1)}>
        {Math.round(z * 100)}%
      </button>
      <IconButton label="Zoom in" kbd="+" onClick={() => camera.zoomStep(1.25)}>
        <Plus size={14} />
      </IconButton>
      {/* fitting the board sits with zooming; All boards has one place, beside the board's name (Features 4, Tidy) */}
      {!simple && (
        <>
          <span className="pill-sep" />
          <IconButton label="See everything" kbd="⇧1" onClick={() => camera.fit(bounds(withMap(boardItems(get()).map(itemRect))), { maxZ: 1 })}>
            <Scan size={14} />
          </IconButton>
        </>
      )}
    </div>
  );
}
